import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import PropTypes from 'prop-types';
import {
  IconSuccess16,
  Toast,
  IconChevronRight16,
  Link,
} from '@stitch-fix/mode-react';
import { SaveBookmark as ModeSaveBookmark } from '@stitch-fix/mode-react/ProductCard';
import { useSelectEvent } from '@stitch-fix/event-reporter';
import { sendCount } from '../../../utils/sendMetrics';

import onError from '../../../utils/onError';

import { useProductDetailsPageContext } from '../../ProductDetailsPage/ProductDetailsPageContext/ProductDetailsPageContext';

import {
  savedItemCreate,
  savedItemDestroy,
  SelectedVariantSavedFragment,
} from './getGatedSaveBookmark.graphql';
import {
  trackSavedItemStatusChange,
  trackSavedItemsToastClick,
} from './SavedItemsTracker';

/**
 * A clickable bookmark icon that adds or removes a sku from a client's saved items.
 *
 * The state of this component is controlled by the `isSaved` prop. On click, this
 * component will trigger a graphql mutation, and update the `sku.saved` field in
 * the apollo cache. This means that parent components that use eg `useQuery`, and
 * pass that data to the `isSaved` prop, should "just work" and not have to store
 * any additional internal state - the apollo cache can be treated as the source of
 * truth.
 */
const SaveBookmark = ({
  isSaved,
  canonicalName,
  onDidChange,
  onWillChange,
  outfitId,
  skuId,
}) => {
  const [activeToast, setActiveToast] = useState(null);
  const [state] = useProductDetailsPageContext();

  const { reportSelectEvent } = useSelectEvent();

  const updateCache = saved => {
    return cache => {
      cache.writeFragment({
        id: `ShopProductVariant:{"sku":"${skuId}"}`,
        fragment: SelectedVariantSavedFragment,
        fragmentName: 'SelectedVariantSavedFragment',
        data: {
          saved,
        },
      });
    };
  };

  const [saveItemMutation, { loading: loadingSave }] = useMutation(
    savedItemCreate,
    {
      onCompleted: () => {
        setActiveToast('saved');
        onDidChange(skuId, true);
      },
      onError: error => {
        onWillChange(skuId, false);
        onError(error);
      },
      update: updateCache(true),
    },
  );

  const [unsaveItemMutation, { loading: loadingUnsave }] = useMutation(
    savedItemDestroy,
    {
      onCompleted: () => {
        setActiveToast('unsaved');
        onDidChange(skuId, false);
      },
      onError: error => {
        onWillChange(skuId, true);
        onError(error);
      },
      update: updateCache(false),
    },
  );

  const onClick = () => {
    const newIsSaved = !isSaved;

    onWillChange(skuId, newIsSaved);

    trackSavedItemStatusChange({
      canonicalName,
      productEventsContext: {
        source: state.source,
        subSource: state.subSource,
        collection: state.collectionName,
      },
      savedStatus: newIsSaved,
      skuId,
      externalProductId: state.externalProductId || '',
    });

    // Proof-of-concept event for v2 atomic events. Should not be relied on
    // for production systems.
    reportSelectEvent({
      schema: 'sku_select',
      name: 'bookmark',
      action_name: newIsSaved ? 'save_item' : 'unsave_item',
      sku_id: skuId,
      external_product_id: state.externalProductId || '',
    });

    sendCount('save_for_later');

    if (newIsSaved) {
      const input = {
        productEventsContext: {
          source: state.source,
          subsource: state.subSource,
          collection: state.collectionName,
        },
        sku: {
          id: skuId,
        },
      };

      if (outfitId) {
        input.outfit = {
          id: outfitId,
        };
      }
      saveItemMutation({
        variables: {
          input,
        },
      });
    } else {
      unsaveItemMutation({
        variables: {
          input: {
            sku: { id: skuId },
          },
        },
      });
    }
  };

  const loading = loadingSave || loadingUnsave;

  const label = isSaved ? 'Remove saved item' : 'Save item';

  return (
    <>
      <ModeSaveBookmark
        isSaved={isSaved}
        aria-label={label}
        onClick={onClick}
        data-testing-id="save-bookmark"
        disabled={loading}
      />

      <Toast
        isOpen={activeToast === 'saved'}
        onClose={() => setActiveToast(null)}
        mb={{ sm: 4, md: 0 }}
        startContent={<IconSuccess16 purpose="decorative" />}
        endContent={
          <Link
            endIcon={<IconChevronRight16 />}
            href="/wardrobe/saved-items?showAvailableItems=true&page=1"
            size="tertiary"
            variant="inherit"
            onClick={() => trackSavedItemsToastClick({ canonicalName })}
          >
            View now
          </Link>
        }
      >
        Saved. Hurry, it’s not reserved.
      </Toast>

      <Toast
        isOpen={activeToast === 'unsaved'}
        onClose={() => setActiveToast(null)}
        mb={{ sm: 4, md: 0 }}
        startContent={<IconSuccess16 purpose="decorative" />}
      >
        Your saved item has been removed.
      </Toast>
    </>
  );
};

SaveBookmark.propTypes = {
  isSaved: PropTypes.bool,
  onDidChange: PropTypes.func,
  onWillChange: PropTypes.func,
  outfitId: PropTypes.string,
  canonicalName: PropTypes.string,
  skuId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
};

SaveBookmark.defaultProps = {
  isSaved: false,
  onDidChange: () => {},
  onWillChange: () => {},
  outfitId: '',
  canonicalName: '',
};

export default SaveBookmark;
